import type { PreOrder } from "@/app/api/types/preOrder";
import { preOrderToTarget } from "../hooks/useGoToPreOrder";
import type { PreOrderTarget } from "./preOrderVisibilityService";
import { getLatestPendingHeaderSuggestion } from "./recapService";

/**
 * Visibility-relevant header fields, mapped from the PO's snake_case column
 * name to the camelCase key the AI tool uses in `ai_suggestions[].changes`.
 * Anything else on the header (`comment`, `hiddenComment`, …) doesn't move
 * the row in or out of any filter so we don't need to overlay it.
 */
const HEADER_FIELDS: Array<{
  snake: keyof PreOrder;
  camel: string;
}> = [
  { snake: "partner_id", camel: "partnerId" },
  { snake: "employee_id", camel: "employeeId" },
  { snake: "location_id", camel: "locationId" },
  { snake: "source_type", camel: "sourceType" },
  { snake: "order_date", camel: "orderDate" },
  { snake: "delivery_date", camel: "deliveryDate" },
  { snake: "status", camel: "status" },
];

function readChangeTo(
  changes: Record<string, { from: unknown; to: unknown; finalTo?: unknown }>,
  camel: string,
  snake: string,
): { has: boolean; value: unknown } {
  // `finalTo` (server-stamped after a user override) wins over `to`. Camel is
  // the canonical key; the snake fallback covers any legacy entries.
  const entry = changes[camel] ?? changes[snake];
  if (!entry || typeof entry !== "object" || !("to" in entry)) {
    return { has: false, value: undefined };
  }
  return {
    has: true,
    value: "finalTo" in entry ? entry.finalTo : entry.to,
  };
}

/**
 * Build the `PreOrderTarget` representing the PO **after** the given approve
 * paths land. Only header-path overlays affect visibility — flow paths don't
 * move the row in or out of any filter dimension, so they're ignored here.
 *
 * Returns the current-state target untouched when:
 * - `approvedPaths` doesn't include `"header"`, OR
 * - the PO carries no pending header suggestion.
 *
 * `resolvePartnerName` (optional) maps the post-accept `partner_id` to the
 * partner's name so the post-accept `partnerName` field matches the new
 * partner — without it, an overlaid `partner_id` would leave `partnerName`
 * bound to the OLD partner's name and the search-filter visibility check
 * would compare against the wrong partner (row can disappear after accept).
 *
 * The visibility primitive (`computeVisibilityPatch`) consumes the result.
 */
export function computePostAcceptTarget(
  preOrder: PreOrder,
  approvedPaths: string[],
  resolvePartnerName?: (partnerId: string | null) => string | undefined,
): PreOrderTarget {
  if (!approvedPaths.includes("header")) {
    return preOrderToTarget(preOrder);
  }

  const pending = getLatestPendingHeaderSuggestion(preOrder);
  if (!pending || !pending.changes) {
    return preOrderToTarget(preOrder);
  }

  const overlaid: Record<string, unknown> = { ...preOrder };
  let partnerIdOverlaid = false;
  for (const { snake, camel } of HEADER_FIELDS) {
    const change = readChangeTo(pending.changes, camel, snake as string);
    if (change.has) {
      overlaid[snake as string] = change.value;
      if (snake === "partner_id") partnerIdOverlaid = true;
    }
  }

  // Resolve `partnerName` against the post-accept `partner_id` when it was
  // overlaid — otherwise `preOrderToTarget` would read the stale `partner.name`
  // attached to the pre-accept PO. Fall back to "" when no resolver is given
  // or the new id can't be resolved, which matches the empty-name behavior of
  // `computeVisibilityPatch.searchQuery` (clears the search filter).
  const overlaidPartnerId = overlaid.partner_id as string | null | undefined;
  const resolvedPartnerName = partnerIdOverlaid
    ? (resolvePartnerName?.(overlaidPartnerId ?? null) ?? "")
    : undefined;

  // `preOrderToTarget` only reads the seven fields enumerated above
  // (plus `partner.name`, untouched on the clone) — the structural cast
  // is safe because the overlay preserves every other field by reference.
  return preOrderToTarget(overlaid as unknown as PreOrder, resolvedPartnerName);
}
